import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { EngineProgram } from '@/utils/rateEngine';

/**
 * The backend's full rate sheet — base rate + today's pricing grid per
 * program — so every screen prices with utils/rateEngine exactly like the
 * backend does. null until the first fetch.
 */
export type HybridRateSheet = {
  programs: Record<string, EngineProgram>;
  termSpreads: { fifteenYear: number | null } | null;
  marketAsOf: string | null;
};

/** Program rates from API; null until first successful fetch. */
export type HybridBackendRates = {
  conventional: number | null;
  fha: number | null;
  va: number | null;
  usda: number | null;
  dscr: number | null;
  commercial: number | null;
  fixAndFlip: number | null;
  bridge: number | null;
  heloc: number | null;
  heloan: number | null;
  secondMortgage: number | null;
  lastUpdated: string | null;
};

// State interface for hybrid interest rate management
export interface HybridInterestRateState {
  backendRates: HybridBackendRates;
  rateSheet: HybridRateSheet | null;
  bridgeRatesByTerm: {
    sixMonth: number;
    twelveMonth: number;
    eighteenMonth: number;
    twentyFourMonth: number;
  } | null;

  localAdjustments: {
    [key in USER_LOAN_PROGRAM]?: {
      baseAdjustment: number;
      creditScoreAdjustment: number;
      ltvAdjustment: number;
      customRate?: number;
    };
  };

  creditScoreAdjustments: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  } | null;

  ltvAdjustments: {
    low: number;
    moderate: number;
    high: number;
    veryHigh: number;
  } | null;

  cacheExpiry: number | null;
  lastFetch: number | null;

  loading: boolean;
  error: string | null;
  isStale: boolean;
}

const emptyBackendRates = (): HybridBackendRates => ({
  conventional: null,
  fha: null,
  va: null,
  usda: null,
  dscr: null,
  commercial: null,
  fixAndFlip: null,
  bridge: null,
  heloc: null,
  heloan: null,
  secondMortgage: null,
  lastUpdated: null,
});

// Initial state — no fabricated rates (Phase A / SOT)
const initialState: HybridInterestRateState = {
  backendRates: emptyBackendRates(),
  rateSheet: null,
  bridgeRatesByTerm: null,
  localAdjustments: {},
  creditScoreAdjustments: null,
  ltvAdjustments: null,
  cacheExpiry: null,
  lastFetch: null,
  loading: false,
  error: null,
  isStale: false,
};

const CACHE_DURATION = 5 * 60 * 1000;

const hybridInterestRateSlice = createSlice({
  name: 'hybridInterestRate',
  initialState,
  reducers: {
    setBackendRates: (
      state,
      action: PayloadAction<{
        conventional: number;
        fha: number;
        va: number;
        usda: number;
        dscr: number;
        commercial: number;
        fixAndFlip: number;
        bridge?: number;
        heloc?: number;
        heloan?: number;
        secondMortgage?: number;
        bridgeRatesByTerm?: {
          sixMonth: number;
          twelveMonth: number;
          eighteenMonth: number;
          twentyFourMonth: number;
        };
        lastUpdated: string;
        programs?: Record<string, EngineProgram>;
        termSpreads?: { fifteenYear: number | null } | null;
        marketAsOf?: string | null;
      }>
    ) => {
      const p = action.payload;
      state.rateSheet = p.programs
        ? {
            programs: p.programs,
            termSpreads: p.termSpreads ?? null,
            marketAsOf: p.marketAsOf ?? null,
          }
        : state.rateSheet;
      state.backendRates = {
        conventional: p.conventional,
        fha: p.fha,
        va: p.va,
        usda: p.usda,
        dscr: p.dscr,
        commercial: p.commercial,
        fixAndFlip: p.fixAndFlip,
        bridge: p.bridge ?? null,
        heloc: p.heloc ?? null,
        heloan: p.heloan ?? null,
        secondMortgage: p.secondMortgage ?? null,
        lastUpdated: p.lastUpdated,
      };
      if (p.bridgeRatesByTerm) {
        state.bridgeRatesByTerm = p.bridgeRatesByTerm;
      }
      state.lastFetch = Date.now();
      state.cacheExpiry = Date.now() + CACHE_DURATION;
      state.isStale = false;
      state.error = null;
    },

    setCreditScoreAdjustments: (
      state,
      action: PayloadAction<{
        excellent: number;
        good: number;
        fair: number;
        poor: number;
      }>
    ) => {
      state.creditScoreAdjustments = action.payload;
    },

    setLTVAdjustments: (
      state,
      action: PayloadAction<{
        low: number;
        moderate: number;
        high: number;
        veryHigh: number;
      }>
    ) => {
      state.ltvAdjustments = action.payload;
    },

    setLocalAdjustment: (
      state,
      action: PayloadAction<{
        loanProgram: USER_LOAN_PROGRAM;
        adjustment: {
          baseAdjustment?: number;
          creditScoreAdjustment?: number;
          ltvAdjustment?: number;
          customRate?: number;
        };
      }>
    ) => {
      const { loanProgram, adjustment } = action.payload;

      if (!state.localAdjustments[loanProgram]) {
        state.localAdjustments[loanProgram] = {
          baseAdjustment: 0,
          creditScoreAdjustment: 0,
          ltvAdjustment: 0,
        };
      }

      state.localAdjustments[loanProgram] = {
        ...state.localAdjustments[loanProgram]!,
        ...adjustment,
      };
    },

    resetLocalAdjustments: (
      state,
      action: PayloadAction<USER_LOAN_PROGRAM>
    ) => {
      const loanProgram = action.payload;
      if (state.localAdjustments[loanProgram]) {
        delete state.localAdjustments[loanProgram];
      }
    },

    resetAllLocalAdjustments: (state) => {
      state.localAdjustments = {};
    },

    markCacheStale: (state) => {
      state.isStale = true;
    },

    clearError: (state) => {
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const {
  setBackendRates,
  setCreditScoreAdjustments,
  setLTVAdjustments,
  setLocalAdjustment,
  resetLocalAdjustments,
  resetAllLocalAdjustments,
  markCacheStale,
  clearError,
  setLoading,
} = hybridInterestRateSlice.actions;

export const selectRateSheet = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.rateSheet;

export const selectBackendRates = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.backendRates;

export const selectLocalAdjustments = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.localAdjustments;

export const selectCreditScoreAdjustments = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.creditScoreAdjustments;

export const selectLTVAdjustments = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.ltvAdjustments;

export const selectCacheExpiry = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.cacheExpiry;

export const selectLastFetch = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.lastFetch;

export const selectIsStale = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.isStale;

export const selectLoading = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.loading;

export const selectError = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.error;

export const selectBridgeRatesByTerm = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => state.hybridInterestRate.bridgeRatesByTerm;

export const selectEffectiveRate =
  (loanProgram: USER_LOAN_PROGRAM) =>
  (state: { hybridInterestRate: HybridInterestRateState }) => {
    const { backendRates, localAdjustments } = state.hybridInterestRate;
    const key = loanProgram.toLowerCase() as keyof HybridBackendRates;
    const raw = backendRates[key];
    const baseRate = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    const adjustments = localAdjustments[loanProgram];

    if (!adjustments) {
      return baseRate;
    }

    let effectiveRate = baseRate;

    if (adjustments.customRate !== undefined) {
      return adjustments.customRate;
    }

    if (adjustments.baseAdjustment) {
      effectiveRate += adjustments.baseAdjustment;
    }

    if (adjustments.creditScoreAdjustment) {
      effectiveRate += adjustments.creditScoreAdjustment;
    }

    if (adjustments.ltvAdjustment) {
      effectiveRate += adjustments.ltvAdjustment;
    }

    return Math.max(0, effectiveRate);
  };

export const selectNeedsRefresh = (state: {
  hybridInterestRate: HybridInterestRateState;
}) => {
  const { cacheExpiry, lastFetch } = state.hybridInterestRate;

  if (!lastFetch || !cacheExpiry) {
    return true;
  }

  return Date.now() > cacheExpiry;
};

export default hybridInterestRateSlice.reducer;
